import { Providers } from "@tripian/model";

type Question = Providers.Livn.Question;
type QuestionGroup = Providers.Livn.QuestionGroup;

export const LIVN_RESOURCE_ANSWER_TYPES = ["FILE", "PDF", "TERMS", "URL", "RESOURCE"];
const NUMERIC_ANSWER_TYPES = ["INTEGER", "NUMBER", "DECIMAL"];

export const isLivnResourceQuestion = (q: Question): boolean =>
  LIVN_RESOURCE_ANSWER_TYPES.includes(q.answerType);

export const isLivnNumericQuestion = (q: Question): boolean =>
  NUMERIC_ANSWER_TYPES.includes(q.answerType);

const matchesRegex = (regex: string | undefined, value: string): boolean => {
  if (!regex) return true;
  try {
    return new RegExp(regex).test(value);
  } catch {
    return true; // malformed regex from API — don't block the user on our own parsing error
  }
};

/**
 * Numeric bounds come from `valueMin`/`valueMax` when the supplier configured them (as strings).
 * Some questions instead state the bounds in prose in the title — e.g. "the passenger count
 * should be a minimum of 1 and a maximum of 10" — and submitting an out-of-range value makes
 * Livn fail the whole step, so that wording is parsed as a fallback.
 */
export const parseLivnNumericBounds = (q: Question): { min?: number; max?: number } => {
  const explicitMin = q.valueMin !== undefined && q.valueMin !== "" ? Number(q.valueMin) : undefined;
  const explicitMax = q.valueMax !== undefined && q.valueMax !== "" ? Number(q.valueMax) : undefined;
  if (Number.isFinite(explicitMin) || Number.isFinite(explicitMax)) {
    return {
      min: Number.isFinite(explicitMin) ? explicitMin : undefined,
      max: Number.isFinite(explicitMax) ? explicitMax : undefined,
    };
  }

  const text = `${q.title ?? ""} ${q.example ?? ""}`.toLowerCase();
  const bounds: { min?: number; max?: number } = {};

  // Sertifikasyon ürünlerindeki gerçek başlık biçimleri: "between 1 and 10",
  // "From 1 to 5, please rate…", "On a scale of 1-10, how well…". Anahtar kelime
  // şartı (between/from/scale of) bilinçli: çıplak "N-M" desenini yakalamak
  // tarih/oda numarası gibi ilgisiz sayı çiftlerini sınır sanardı.
  const range = text.match(
    /(?:between|from|scale\s+of)\s+(\d+(?:\.\d+)?)\s*(?:and|to|[-–])\s*(\d+(?:\.\d+)?)/
  );
  if (range) {
    bounds.min = Number(range[1]);
    bounds.max = Number(range[2]);
  }

  const min = text.match(/(?:minimum(?:\s+of)?|min\.?|at\s+least|no\s+less\s+than)\s*(\d+(?:\.\d+)?)/);
  if (min) bounds.min = Number(min[1]);

  const max = text.match(/(?:maximum(?:\s+of)?|max\.?|at\s+most|no\s+more\s+than|up\s+to)\s*(\d+(?:\.\d+)?)/);
  if (max) bounds.max = Number(max[1]);

  // A bogus parse (min above max) is worse than no parse at all — drop both.
  if (bounds.min !== undefined && bounds.max !== undefined && bounds.min > bounds.max) return {};

  if (bounds.min === undefined) {
    // A quantity of zero is never a valid selection, but 0 is legitimate elsewhere (an infant's
    // age, a weight) — so only counts get a floor of 1; everything else just can't go negative.
    const isCount = /\b(count|quantity|qty|number of|how many|passengers?|pax|people|persons?|adults?|children|infants?|guests?|travellers?|travelers?|bags?|units?|tickets?)\b/.test(
      text
    );
    bounds.min = isCount ? 1 : 0;
  }

  return bounds;
};

const looksLikePhoneQuestion = (q: Question): boolean => {
  const text = `${q.purpose ?? ""} ${q.title ?? ""}`.toLowerCase();
  return /phone|mobile|cell|whatsapp|contact number|telephone/.test(text);
};

const looksLikeEmailQuestion = (q: Question): boolean => {
  const text = `${q.purpose ?? ""} ${q.title ?? ""}`.toLowerCase();
  return /e-?mail/.test(text);
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
/** E.164-ish: optional +, 7–15 digits, common separators allowed for readability. */
const PHONE_REGEX = /^\+?[\d][\d\s\-().]{5,20}$/;

/**
 * Returns a user-facing error for a single answer, or undefined when it is acceptable.
 * Used both to gate the Continue button and to render inline field errors.
 */
export const validateLivnAnswer = (q: Question, rawValue: string): string | undefined => {
  const value = (rawValue ?? "").trim();

  if (isLivnResourceQuestion(q)) {
    return q.required && !value ? "Please read and accept to continue." : undefined;
  }

  if (!value) return q.required ? "This field is required." : undefined;

  if (isLivnNumericQuestion(q)) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "Please enter a number.";
    if (q.answerType !== "DECIMAL" && !Number.isInteger(num)) return "Please enter a whole number.";
    const { min, max } = parseLivnNumericBounds(q);
    if (min !== undefined && num < min) return `Please enter ${min} or more.`;
    if (max !== undefined && num > max) return `Please enter ${max} or less.`;
    return undefined;
  }

  if (q.answerType === "EMAIL" || looksLikeEmailQuestion(q)) {
    if (!EMAIL_REGEX.test(value)) return "Please enter a valid email address (e.g. name@example.com).";
    return undefined;
  }

  if (q.answerType === "PHONE" || looksLikePhoneQuestion(q)) {
    const digits = value.replace(/\D/g, "");
    if (!PHONE_REGEX.test(value) || digits.length < 7 || digits.length > 15) {
      return "Please enter a valid mobile number including country code (e.g. +61 400 000 000).";
    }
    return undefined;
  }

  if (q.answerType === "DATE" && Number.isNaN(new Date(value).getTime())) return "Please enter a valid date.";

  if (q.answerType === "TIME" && !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return "Please enter a valid time (HH:MM).";

  // Uzunluk sınırları: Livn bunları sayı olarak veriyor (ör. 10 haneli frequent flyer
  // numarası lengthMin=lengthMax=10). Regex kontrolünden önce bakılıyor ki kullanıcı
  // "geçersiz değer" yerine kaç karakter gerektiğini görsün.
  if (q.lengthMin !== undefined && q.lengthMax !== undefined && q.lengthMin === q.lengthMax) {
    if (value.length !== q.lengthMin) return `Please enter exactly ${q.lengthMin} characters.`;
  } else {
    if (q.lengthMin !== undefined && value.length < q.lengthMin)
      return `Please enter at least ${q.lengthMin} characters.`;
    if (q.lengthMax !== undefined && value.length > q.lengthMax)
      return `Please enter at most ${q.lengthMax} characters.`;
  }

  if (!matchesRegex(q.regex, value)) {
    return q.example ? `Please match the expected format (e.g. ${q.example}).` : "Please enter a valid value.";
  }

  return undefined;
};

/**
 * Bir seçim sorusunda seçilen seçeneğin ortaya çıkardığı ek soru grupları.
 *
 * Livn koşullu soruları ayrı bir akış adımı olarak göndermiyor; seçeneğin içine
 * `followUpQuestions` olarak gömüyor. Seçenek değişince ortaya çıkan sorular da
 * değiştiği için hem render hem doğrulama hem de gönderilen cevaplar bu listeden
 * türetilmeli.
 */
export const livnFollowUpGroups = (
  q: Question,
  selectedValue?: string
): QuestionGroup[] => {
  if (!selectedValue) return [];
  // SELECT_MULTIPLE virgülle birden fazla seçenek tutabiliyor
  const selectedUuids = selectedValue.split(",").filter(Boolean);
  return (q.selectOptions || [])
    .filter(option => selectedUuids.includes(option.uuid))
    .flatMap(option => option.followUpQuestions?.questionGroups || []);
};

/** Bir soru listesini, seçili cevaplara göre açılan takip sorularıyla birlikte düzleştirir. */
export const livnQuestionsWithFollowUps = (
  questions: Question[],
  answers: Record<string, string>
): Question[] =>
  questions.flatMap(q => [
    q,
    ...livnFollowUpGroups(q, answers[q.uuid]).flatMap(g =>
      livnQuestionsWithFollowUps(g.questions, answers)
    ),
  ]);

/**
 * Extra guidance shown under a field so the customer knows what is expected — the raw
 * Livn question title is often just a bare label ("Given name") with no format hint.
 */
export const livnAnswerHint = (q: Question): string | undefined => {
  const parts: string[] = [];

  // The supplier's own guidance always wins — it is written for this exact field.
  if (q.description) parts.push(q.description);
  if (q.feeDescription) parts.push(q.feeDescription);

  if (isLivnNumericQuestion(q)) {
    const { min, max } = parseLivnNumericBounds(q);
    // A floor of 0 is the implicit "can't be negative" default, not information worth showing.
    const showMin = min !== undefined && min > 0;
    if (showMin && max !== undefined) parts.push(`Enter a number between ${min} and ${max}`);
    else if (showMin) parts.push(`Minimum ${min}`);
    else if (max !== undefined) parts.push(`Maximum ${max}`);
  } else if (q.answerType === "EMAIL" || looksLikeEmailQuestion(q)) {
    parts.push("Booking confirmation and tickets are sent to this address");
  } else if (q.answerType === "PHONE" || looksLikePhoneQuestion(q)) {
    parts.push("Include the country code so the supplier can reach you on the day");
  } else if (q.answerType === "DATE") {
    parts.push("Format: YYYY-MM-DD");
  }

  if (q.lengthMin !== undefined && q.lengthMin === q.lengthMax) {
    parts.push(`${q.lengthMin} characters`);
  } else if (q.lengthMax !== undefined) {
    parts.push(`Up to ${q.lengthMax} characters`);
  }

  if (q.example && !isLivnNumericQuestion(q)) parts.push(`Example: ${q.example}`);

  return parts.length > 0 ? parts.join(" · ") : undefined;
};

/**
 * Livn surfaces backend failures verbatim — including strings that are meaningless or
 * alarming to a customer ("A required dependency failed", "Unable to generate price
 * preview due to: null"). Anything that looks internal is replaced with a friendly
 * message; genuinely customer-oriented text is passed through unchanged.
 */
const INTERNAL_ERROR_PATTERNS = [
  /required dependency/i,
  /due to:\s*null/i,
  /unexpected error/i,
  /internal (server )?error/i,
  /\bnull\b/i,
  /\bundefined\b/i,
  /exception|stacktrace|stack trace/i,
  /\bjava\.|\bcom\.|\borg\.springframework/i,
  /\b(4\d{2}|5\d{2})\s*(error|status)/i,
  /sql|constraint violation|deadlock/i,
];

export const FRIENDLY_LIVN_ERROR =
  "We couldn't complete this step. Please review your selections and details, then try again.";

export const sanitizeLivnErrorMessage = (message?: string, fallback: string = FRIENDLY_LIVN_ERROR): string => {
  const text = (message ?? "").trim();
  if (!text) return fallback;
  if (INTERNAL_ERROR_PATTERNS.some(p => p.test(text))) return fallback;
  return text;
};

/**
 * Livn's `customerErrorMessage` is not safe to show as-is: every observed value prefixes the real
 * problem with internal phrasing ("An error occurred while retrieving passenger questions: …",
 * "Validation failed: … does not match regex..", "Flow terminated as per the request: …").
 *
 * Rather than pattern-matching what to strip, the recognised failures are re-expressed in our own
 * words — so the customer gets an actionable instruction — and anything unrecognised falls back to
 * a generic message. Livn's text is never passed through verbatim.
 */
export const mapLivnFlowError = (error?: {
  customerErrorMessage?: string;
  internalErrorMessage?: string;
  terminateTransaction?: boolean;
}): string => {
  const raw = `${error?.internalErrorMessage ?? ""} ${error?.customerErrorMessage ?? ""}`;

  const sizeMismatch = raw.match(/booking size \((\d+)\) must be equal to the entered passenger count \((\d+)\)/i);
  if (sizeMismatch) {
    const [, selected, declared] = sizeMismatch;
    return `Please select tickets for exactly ${declared} ${
      declared === "1" ? "traveller" : "travellers"
    } — you currently have ${selected}.`;
  }

  const regexFailure = raw.match(/Validation failed:\s*([^:]+):\s*String does not match regex/i);
  if (regexFailure) {
    return `${regexFailure[1].trim()} is not in the format the supplier expects. Please check it and try again.`;
  }

  const validationFailure = raw.match(/Validation failed:\s*([^:]+):\s*(.+?)(?:\.\.|\.\s|$)/i);
  if (validationFailure) {
    return `${validationFailure[1].trim()}: ${validationFailure[2].trim()}. Please review this field and try again.`;
  }

  if (/no longer available|sold out|insufficient availability|availability/i.test(raw)) {
    return "That option is no longer available for this date. Please choose a different time or date.";
  }

  if (/expired|expiry|timed out/i.test(raw)) {
    return "This booking step timed out. Please start again to get current prices and availability.";
  }

  if (error?.terminateTransaction) {
    return "We couldn't confirm this booking with the supplier. Please start again or try a different date.";
  }

  return FRIENDLY_LIVN_ERROR;
};
